import { AuthError, StepUpFailedError } from "./errors";
import { hasWebAuthn } from "./device-capabilities";

/**
 * WebAuthn ceremony state machine — framework-agnostic, shared
 * between every PureID SPA.
 *
 * This module provides:
 *
 *   1. `CeremonyState` / `CeremonyAction` — a pure discriminated
 *      union describing exactly one ceremony in flight.
 *   2. `ceremonyReducer` — pure (state, action) → state.
 *      Every transition is explicit and enumerable; invalid
 *      transitions return the current state unchanged so bad
 *      dispatches can be detected in tests.
 *   3. `runAssertion` / `runAttestation` — side-effectful runners
 *      that drive the ceremony against the DOM credentials API
 *      and dispatch actions into a caller-provided reducer.
 *   4. Buffer ↔ base64url codecs used by both sides of the wire.
 *
 * Design constraints:
 *   - Zero React imports. Each app wraps the reducer + runners in
 *     its own thin hook.
 *   - Zero direct calls to any api client. The runner takes a
 *     caller-supplied `begin` / `complete` pair so the same
 *     machine is usable for admin login, tenant login, enrollment,
 *     recovery, and step-up — each of which hits a different
 *     backend endpoint.
 */

// ── Base64url codec ──────────────────────────────────────────────────────

export function base64urlEncode(buf: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
}

export function base64urlDecode(s: string): Uint8Array<ArrayBuffer> {
  const padded = s
    .replace(/-/g, "+")
    .replace(/_/g, "/")
    .padEnd(Math.ceil(s.length / 4) * 4, "=");
  return new Uint8Array(
    Array.from(atob(padded), (c) => c.charCodeAt(0)),
  );
}

// ── Wire formats (what the backend sends / expects) ─────────────────────

export interface AssertionOptionsWire {
  challenge: string;
  rpId?: string;
  timeout?: number;
  userVerification?: UserVerificationRequirement;
  allowCredentials?: Array<{
    type: "public-key";
    id: string;
    transports?: AuthenticatorTransport[];
  }>;
  hints?: string[];
  extensions?: AuthenticationExtensionsClientInputs;
}

export interface AttestationOptionsWire {
  challenge: string;
  rp: { id: string; name: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: PublicKeyCredentialParameters[];
  timeout?: number;
  attestation?: AttestationConveyancePreference;
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  excludeCredentials?: Array<{
    type: "public-key";
    id: string;
    transports?: AuthenticatorTransport[];
  }>;
  hints?: string[];
  extensions?: AuthenticationExtensionsClientInputs;
}

export interface AssertionResultWire {
  id: string;
  rawId: string;
  type: "public-key";
  response: {
    clientDataJSON: string;
    authenticatorData: string;
    signature: string;
    userHandle: string | null;
  };
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
  authenticatorAttachment?: AuthenticatorAttachment | null;
}

export interface AttestationResultWire {
  id: string;
  rawId: string;
  type: "public-key";
  response: {
    clientDataJSON: string;
    attestationObject: string;
    transports: AuthenticatorTransport[];
  };
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
  authenticatorAttachment?: AuthenticatorAttachment | null;
}

// ── Browser-native options builders (decode wire → DOM) ─────────────────

type RequestOptionsWithHints = PublicKeyCredentialRequestOptions & {
  hints?: string[];
};
type CreationOptionsWithHints = PublicKeyCredentialCreationOptions & {
  hints?: string[];
};

function decodeAssertionOptions(wire: AssertionOptionsWire): RequestOptionsWithHints {
  const options: RequestOptionsWithHints = {
    challenge: base64urlDecode(wire.challenge),
    ...(wire.rpId !== undefined && { rpId: wire.rpId }),
    ...(wire.timeout !== undefined && { timeout: wire.timeout }),
    ...(wire.userVerification !== undefined && { userVerification: wire.userVerification }),
    ...(wire.extensions !== undefined && { extensions: wire.extensions }),
  };
  if (wire.allowCredentials) {
    options.allowCredentials = wire.allowCredentials.map((c) => ({
      type: c.type,
      id: base64urlDecode(c.id),
      ...(c.transports ? { transports: c.transports } : {}),
    }));
  }
  if (wire.hints) options.hints = wire.hints;
  return options;
}

function decodeAttestationOptions(wire: AttestationOptionsWire): CreationOptionsWithHints {
  const options: CreationOptionsWithHints = {
    challenge: base64urlDecode(wire.challenge),
    rp: wire.rp,
    user: {
      id: base64urlDecode(wire.user.id),
      name: wire.user.name,
      displayName: wire.user.displayName,
    },
    pubKeyCredParams: wire.pubKeyCredParams,
    ...(wire.timeout !== undefined && { timeout: wire.timeout }),
    ...(wire.attestation !== undefined && { attestation: wire.attestation }),
    ...(wire.authenticatorSelection !== undefined && {
      authenticatorSelection: wire.authenticatorSelection,
    }),
    ...(wire.extensions !== undefined && { extensions: wire.extensions }),
  };
  if (wire.excludeCredentials) {
    options.excludeCredentials = wire.excludeCredentials.map((c) => ({
      type: c.type,
      id: base64urlDecode(c.id),
      ...(c.transports ? { transports: c.transports } : {}),
    }));
  }
  if (wire.hints) options.hints = wire.hints;
  return options;
}

// ── Result encoders (DOM → wire) ────────────────────────────────────────

// Narrow the lib.dom return types — depending on the TypeScript
// version, `authenticatorAttachment` is typed as either
// `AuthenticatorAttachment | null` or the widened `string | null`,
// and `getTransports()` as either `AuthenticatorTransport[]` or
// `string[]`. Validate by whitelist so downstream code always sees
// the WebAuthn enum type.
const VALID_ATTACHMENTS: readonly AuthenticatorAttachment[] = [
  "platform",
  "cross-platform",
];
const VALID_TRANSPORTS: readonly AuthenticatorTransport[] = [
  "usb",
  "nfc",
  "ble",
  "hybrid",
  "internal",
];

function normalizeAttachment(raw: string | null): AuthenticatorAttachment | null {
  if (raw === null) return null;
  return VALID_ATTACHMENTS.includes(raw as AuthenticatorAttachment)
    ? (raw as AuthenticatorAttachment)
    : null;
}

function normalizeTransports(raw: readonly string[]): AuthenticatorTransport[] {
  return raw.filter((t): t is AuthenticatorTransport =>
    VALID_TRANSPORTS.includes(t as AuthenticatorTransport),
  );
}

function encodeAssertion(cred: PublicKeyCredential): AssertionResultWire {
  const response = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: base64urlEncode(cred.rawId),
    type: "public-key",
    response: {
      clientDataJSON: base64urlEncode(response.clientDataJSON),
      authenticatorData: base64urlEncode(response.authenticatorData),
      signature: base64urlEncode(response.signature),
      userHandle: response.userHandle ? base64urlEncode(response.userHandle) : null,
    },
    clientExtensionResults: cred.getClientExtensionResults(),
    authenticatorAttachment: normalizeAttachment(cred.authenticatorAttachment),
  };
}

export function encodeAttestation(cred: PublicKeyCredential): AttestationResultWire {
  const response = cred.response as AuthenticatorAttestationResponse;
  // Transports are the authenticator's self-report of its supported
  // channels. Pulling them from getTransports() is the ONLY correct
  // path — never default to a fixed list, which is a known footgun
  // that breaks hybrid/QR flows.
  const rawTransports =
    typeof response.getTransports === "function" ? response.getTransports() : [];
  return {
    id: cred.id,
    rawId: base64urlEncode(cred.rawId),
    type: "public-key",
    response: {
      clientDataJSON: base64urlEncode(response.clientDataJSON),
      attestationObject: base64urlEncode(response.attestationObject),
      transports: normalizeTransports(rawTransports),
    },
    clientExtensionResults: cred.getClientExtensionResults(),
    authenticatorAttachment: normalizeAttachment(cred.authenticatorAttachment),
  };
}

// ── State machine ────────────────────────────────────────────────────────

export type CeremonyKind = "assertion" | "attestation";

export type CeremonyState =
  | { status: "idle" }
  | { status: "challenging"; kind: CeremonyKind }
  | { status: "awaiting-user"; kind: CeremonyKind }
  | { status: "verifying"; kind: CeremonyKind }
  | { status: "success"; kind: CeremonyKind }
  | {
      status: "error";
      kind: CeremonyKind;
      code: CeremonyErrorCode;
      message: string;
    };

export type CeremonyErrorCode =
  | "begin_failed"
  | "user_cancelled"
  | "ceremony_failed"
  | "verify_failed"
  | "not_supported"
  | "timeout";

export type CeremonyAction =
  | { type: "begin"; kind: CeremonyKind }
  | { type: "prompt" }
  | { type: "verify" }
  | { type: "success" }
  | { type: "fail"; code: CeremonyErrorCode; message: string }
  | { type: "reset" };

export function ceremonyReducer(state: CeremonyState, action: CeremonyAction): CeremonyState {
  switch (action.type) {
    case "begin":
      return { status: "challenging", kind: action.kind };
    case "prompt":
      if (state.status !== "challenging") return state;
      return { status: "awaiting-user", kind: state.kind };
    case "verify":
      if (state.status !== "awaiting-user") return state;
      return { status: "verifying", kind: state.kind };
    case "success":
      if (state.status !== "verifying") return state;
      return { status: "success", kind: state.kind };
    case "fail": {
      const kind = "kind" in state ? state.kind : "assertion";
      return { status: "error", kind, code: action.code, message: action.message };
    }
    case "reset":
      return { status: "idle" };
  }
}

export const initialCeremonyState: CeremonyState = { status: "idle" };

// ── Runners (side-effectful; dispatch against a reducer) ────────────────

type Dispatch = (action: CeremonyAction) => void;

/**
 * Run an assertion ceremony end-to-end.
 *
 * Callers provide `begin` (fetch options from the backend) and
 * `complete` (post the signed assertion, return whatever the flow
 * produces). The runner dispatches state transitions as it
 * progresses. On failure it rejects with a typed `CeremonyError`
 * AND dispatches `fail`.
 */
export async function runAssertion<T>(
  dispatch: Dispatch,
  begin: () => Promise<AssertionOptionsWire>,
  complete: (signed: AssertionResultWire) => Promise<T>,
  signal?: AbortSignal,
): Promise<T> {
  if (signal?.aborted) throw new CeremonyError("user_cancelled", "Ceremony cancelled.");
  // The SAME predicate `capability.webauthn` reports (hasWebAuthn)
  // — the no-WebAuthn preflight surface pre-blocks on exactly this
  // condition, so the two can never disagree.
  if (!hasWebAuthn()) {
    const err = {
      code: "not_supported" as const,
      message: "WebAuthn is not supported in this browser.",
    };
    dispatch({ type: "fail", ...err });
    throw new CeremonyError(err.code, err.message);
  }

  dispatch({ type: "begin", kind: "assertion" });

  let wire: AssertionOptionsWire;
  try {
    wire = await begin();
  } catch (err) {
    // AuthError from the begin endpoint means the caller's
    // session died between whatever 403-ed and this ceremony
    // starting. Don't wrap it in CeremonyError — that would hide
    // the 401 from the SPA's global SignedOutScreen handler.
    // Reset the reducer (so the ceremony dialog doesn't show a
    // stale failure message on close) and rethrow the original
    // AuthError verbatim.
    if (err instanceof AuthError) {
      dispatch({ type: "reset" });
      throw err;
    }
    const msg = err instanceof Error ? err.message : "Failed to start assertion.";
    dispatch({ type: "fail", code: "begin_failed", message: msg });
    throw new CeremonyError("begin_failed", msg, err);
  }

  if (signal?.aborted) throw new CeremonyError("user_cancelled", "Ceremony cancelled.");
  dispatch({ type: "prompt" });
  const options = decodeAssertionOptions(wire);

  let credential: PublicKeyCredential | null;
  try {
    credential = (await navigator.credentials.get({
      publicKey: options,
      signal,
    })) as PublicKeyCredential | null;
  } catch (err) {
    const typed = classifyCredentialError(err);
    dispatch({ type: "fail", ...typed });
    throw new CeremonyError(typed.code, typed.message, err);
  }

  if (signal?.aborted) throw new CeremonyError("user_cancelled", "Ceremony cancelled.");
  if (!credential) {
    const msg = "No credential returned by the authenticator.";
    dispatch({ type: "fail", code: "ceremony_failed", message: msg });
    throw new CeremonyError("ceremony_failed", msg);
  }

  dispatch({ type: "verify" });
  try {
    const result = await complete(encodeAssertion(credential));
    dispatch({ type: "success" });
    return result;
  } catch (err) {
    // Same rule as begin(): if the verify endpoint 401s, pass the
    // AuthError through untouched so the global SignedOutScreen
    // fires instead of a dead "Server rejected the assertion"
    // message in a stuck dialog.
    //
    // StepUpFailedError is the recoverable counterpart — backend
    // stepup_handler rejected THIS specific assertion (wrong
    // passkey, signature invalid, user mismatch) but the main
    // session is still good. Pass it through untouched so the
    // step-up surface can show inline error ("wrong passkey,
    // try again") instead of routing to SignedOutScreen.
    if (err instanceof AuthError || err instanceof StepUpFailedError) {
      dispatch({ type: "reset" });
      throw err;
    }
    const msg = err instanceof Error ? err.message : "Server rejected the assertion.";
    dispatch({ type: "fail", code: "verify_failed", message: msg });
    throw new CeremonyError("verify_failed", msg, err);
  }
}

/**
 * Run an attestation (registration) ceremony end-to-end. Mirror
 * of `runAssertion` but for `navigator.credentials.create`.
 */
export async function runAttestation<T>(
  dispatch: Dispatch,
  begin: () => Promise<AttestationOptionsWire>,
  complete: (signed: AttestationResultWire) => Promise<T>,
  signal?: AbortSignal,
): Promise<T> {
  if (signal?.aborted) throw new CeremonyError("user_cancelled", "Ceremony cancelled.");
  // Same shared predicate as runAssertion — see the note there.
  if (!hasWebAuthn()) {
    const err = {
      code: "not_supported" as const,
      message: "WebAuthn is not supported in this browser.",
    };
    dispatch({ type: "fail", ...err });
    throw new CeremonyError(err.code, err.message);
  }

  dispatch({ type: "begin", kind: "attestation" });

  let wire: AttestationOptionsWire;
  try {
    wire = await begin();
  } catch (err) {
    if (err instanceof AuthError) {
      dispatch({ type: "reset" });
      throw err;
    }
    const msg = err instanceof Error ? err.message : "Failed to start registration.";
    dispatch({ type: "fail", code: "begin_failed", message: msg });
    throw new CeremonyError("begin_failed", msg, err);
  }

  if (signal?.aborted) throw new CeremonyError("user_cancelled", "Ceremony cancelled.");
  dispatch({ type: "prompt" });
  const options = decodeAttestationOptions(wire);

  let credential: PublicKeyCredential | null;
  try {
    credential = (await navigator.credentials.create({
      publicKey: options,
      signal,
    })) as PublicKeyCredential | null;
  } catch (err) {
    const typed = classifyCredentialError(err);
    dispatch({ type: "fail", ...typed });
    throw new CeremonyError(typed.code, typed.message, err);
  }

  if (signal?.aborted) throw new CeremonyError("user_cancelled", "Ceremony cancelled.");
  if (!credential) {
    const msg = "No credential created by the authenticator.";
    dispatch({ type: "fail", code: "ceremony_failed", message: msg });
    throw new CeremonyError("ceremony_failed", msg);
  }

  dispatch({ type: "verify" });
  try {
    const result = await complete(encodeAttestation(credential));
    dispatch({ type: "success" });
    return result;
  } catch (err) {
    if (err instanceof AuthError) {
      dispatch({ type: "reset" });
      throw err;
    }
    const msg = err instanceof Error ? err.message : "Server rejected the attestation.";
    dispatch({ type: "fail", code: "verify_failed", message: msg });
    throw new CeremonyError("verify_failed", msg, err);
  }
}

// ── Error classification ────────────────────────────────────────────────

/**
 * CeremonyError is the typed error every runner throws when a
 * ceremony fails for a reason mapped to one of the legacy
 * `CeremonyErrorCode` values. The original underlying error
 * (DOMException, network Error, server-side ApiError) is
 * preserved on `.cause` so the page-level catch site can run
 * the richer `classifyCeremonyFailure` (with duration +
 * capability heuristics) against the real DOMException rather
 * than against this typed wrapper.
 *
 * `.cause` is a standard ECMAScript Error field and is
 * preserved by Error.prototype.toString() / structured-clone,
 * so its presence here is non-breaking.
 */
export class CeremonyError extends Error {
  constructor(
    public readonly code: CeremonyErrorCode,
    message: string,
    cause?: unknown,
  ) {
    super(message, cause === undefined ? undefined : { cause });
    this.name = "CeremonyError";
  }
}

function classifyCredentialError(err: unknown): {
  code: CeremonyErrorCode;
  message: string;
} {
  if (err instanceof DOMException) {
    switch (err.name) {
      case "NotAllowedError":
        return {
          code: "user_cancelled",
          message: "The prompt was dismissed or the authenticator refused.",
        };
      case "TimeoutError":
        return {
          code: "timeout",
          message: "The authenticator did not respond in time.",
        };
      case "InvalidStateError":
        return {
          code: "ceremony_failed",
          message: "This authenticator is already registered.",
        };
      case "NotSupportedError":
        return {
          code: "not_supported",
          message: "This browser does not support the requested authenticator type.",
        };
      default:
        return { code: "ceremony_failed", message: err.message || err.name };
    }
  }
  if (err instanceof Error) return { code: "ceremony_failed", message: err.message };
  return { code: "ceremony_failed", message: "Unknown authenticator error." };
}

// ── Conditional mediation (passkey autofill) ───────────────────────────

/**
 * Checks whether the browser supports conditional mediation
 * (`mediation: 'conditional'` on `navigator.credentials.get`).
 * Returns `false` on any error or in environments where the
 * API is unavailable. Safe to call in SSR / non-browser.
 */
export async function supportsConditionalMediation(): Promise<boolean> {
  try {
    if (typeof window === "undefined") return false;
    if (!window.PublicKeyCredential) return false;
    const fn = (PublicKeyCredential as unknown as {
      isConditionalMediationAvailable?: () => Promise<boolean>;
    }).isConditionalMediationAvailable;
    return fn ? await fn() : false;
  } catch {
    return false;
  }
}

/**
 * Start a conditional mediation assertion. The promise stays
 * pending until the user selects a passkey from the browser's
 * autofill dropdown. Returns the encoded assertion on success,
 * `null` on any error (per W3C spec, conditional requests should
 * not surface errors — all failure cases are intentionally
 * indistinguishable for privacy).
 *
 * The caller MUST pass an `AbortSignal` and abort it before
 * starting any modal `navigator.credentials.get` call (the two
 * cannot coexist per spec).
 */
export async function runConditionalAssertion(
  wire: AssertionOptionsWire,
  signal: AbortSignal,
): Promise<AssertionResultWire | null> {
  try {
    const options = decodeAssertionOptions(wire);
    const credential = (await navigator.credentials.get({
      publicKey: options,
      mediation: "conditional" as CredentialMediationRequirement,
      signal,
    })) as PublicKeyCredential | null;
    if (!credential) return null;
    return encodeAssertion(credential);
  } catch {
    return null;
  }
}
